import { useEffect, useState } from "react";
import { CheckCircle2, Mail, RotateCcw, XCircle } from "lucide-react";
import { toast } from "sonner";
import api, { errMsg } from "@/lib/api";
import { fmtDate, fmtDateTime } from "@/lib/constants";
import { Card, LoadingBlock, Spinner } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const slug = (s) => s.toLowerCase().replace(/[^a-z]+/g, "-");

const Recipients = ({ r, onSaved }) => {
  const [v, setV] = useState(() => Object.fromEntries(Object.entries(r.recipients).map(([k, e]) => [k, e.join(", ")])));
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      await api.put("/reports/recipients", { recipients: v });
      toast.success("Email addresses saved");
      onSaved();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Card title="Who gets the Monday reports" subtitle="Every Monday at 9:00 AM, last week's (Mon–Sun) complaints go to each brand's FOH and BOH heads: 6 reports. Separate several emails with commas." testid="report-recipients-card">
      <div className="grid gap-5 lg:grid-cols-3">
        {r.brands.map((b) => (
          <div key={b} className="space-y-3 rounded-2xl border border-slate-200 p-4">
            <div className="font-semibold text-slate-900">{b}</div>
            {Object.entries(r.departments).map(([d, label]) => (
              <div key={d} className="space-y-1.5">
                <Label>{label} head</Label>
                <Input className="h-10 rounded-xl" placeholder="name@ninis.co.in" value={v[`${b}|${d}`] || ""} onChange={(e) => setV({ ...v, [`${b}|${d}`]: e.target.value })} data-testid={`recipients-${slug(b)}-${d.toLowerCase()}`} />
              </div>
            ))}
          </div>
        ))}
      </div>
      <Button className="mt-5 h-11 rounded-xl" onClick={save} disabled={busy} data-testid="recipients-save-button">{busy && <Spinner className="mr-2 h-4 w-4" />}Save email addresses</Button>
    </Card>
  );
};

const History = ({ r, send }) => (
  <Card title="Sent reports" subtitle="Resend rebuilds the report with the latest data and emails it again." testid="report-history-card">
    <div className="mb-4 flex flex-wrap items-center gap-3">
      <Button className="h-10 rounded-xl" onClick={() => send({})} disabled={r.running} data-testid="send-last-week-button">
        {r.running ? <Spinner className="mr-2 h-4 w-4" /> : <Mail className="mr-2 h-4 w-4" />}{r.running ? "Sending…" : `Send all 6 for ${fmtDate(r.last_week.from)} – ${fmtDate(r.last_week.to)} now`}
      </Button>
    </div>
    {!r.history.length ? <p className="text-sm text-slate-400">No reports sent yet.</p> : (
      <ul className="divide-y divide-slate-100 text-sm" data-testid="report-history-list">
        {r.history.map((h) => (
          <li key={h.id} className="flex flex-wrap items-start justify-between gap-3 py-3" data-testid={`report-row-${h.id}`}>
            <div className="flex gap-3">
              {h.status === "sent" ? <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-500" /> : <XCircle className="mt-0.5 h-4 w-4 shrink-0 text-red-500" />}
              <div>
                <div className="font-medium text-slate-900">{h.brand} · {r.departments[h.dept]} · {fmtDate(h.week_from)} – {fmtDate(h.week_to)}</div>
                <div className="text-slate-600">{h.message}</div>
                <div className="text-xs text-slate-400">{fmtDateTime(h.at)} · {h.trigger === "auto" ? "Automatic" : h.by}</div>
              </div>
            </div>
            <Button size="sm" variant="outline" className="rounded-lg" disabled={r.running} onClick={() => send({ week_from: h.week_from, brand: h.brand, dept: h.dept })} data-testid={`report-resend-${h.id}`}><RotateCcw className="mr-1.5 h-3.5 w-3.5" />Resend</Button>
          </li>
        ))}
      </ul>
    )}
  </Card>
);

export default function ReportsTab() {
  const [r, setR] = useState(null);
  const load = () => api.get("/reports").then((x) => setR(x.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => {
    load();
  }, []);
  useEffect(() => {
    if (!r?.running) return undefined;
    const id = setInterval(load, 5000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [r?.running]);
  const send = async (body) => {
    try {
      const { data } = await api.post("/reports/send", body);
      toast.success(data.message);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  if (!r) return <LoadingBlock />;
  return (
    <div className="space-y-6" data-testid="reports-tab">
      <Recipients r={r} onSaved={load} />
      <History r={r} send={send} />
    </div>
  );
}
